import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { AppPillSwitch } from '../../components/ui';
import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import { AppFilterBar, AppFilterField, AppFilterInput, AppFilterSelect } from '../../components/filters';
import { notify } from '../../components/common/AppToast';
import {
  AppButton,
  AppCheckbox,
  AppFormField,
  AppFormLabel,
  AppInput,
  AppSelect,
  AppTextarea,
} from '../../components/ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import itiMaintenanceService from '../../services/itiMaintenanceService';
import {
  ItiMaintenanceConfirmModal,
  ItiMaintenanceEmailEditor,
  ItiMaintenanceHistoryModal,
} from './ItiMaintenanceMailModals';
import {
  STORAGE_REPLY_TO,
  STORAGE_TEST_EMAIL,
  VARIABLES,
  formatDateFr,
  groupRecipients,
  readStored,
  writeStored,
} from './itiMaintenanceMails.helpers';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-planning-view-mode.scss';
import '../../styles/iti-maintenance-page.scss';

const toggleValue = (list, value) => (
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value]
);

const emptyFilters = () => ({
  search: '',
  servers: [],
  plugins: [],
  tags: [],
  hasEmail: null,
  hasClient: null,
});

const ItiMaintenanceMailsPage = () => {
  const { ask, modal } = useAppConfirm();
  const [tab, setTab] = useState('destinataires');
  const [sites, setSites] = useState([]);
  const [facets, setFacets] = useState({ servers: [], tags: [], plugins: [] });
  const [templates, setTemplates] = useState([]);
  const [envois, setEnvois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [searchInput, setSearchInput] = useState('');
  const [pluginQuery, setPluginQuery] = useState('');
  const [basket, setBasket] = useState(() => new Map());
  const [emailOverrides, setEmailOverrides] = useState({});
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('Bonjour {{client}},\n\nNous vous informons d’une intervention de maintenance sur le site {{site}} ({{url}}).\n\nCordialement,\nl’équipe itiConseil');
  const [templateId, setTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [testEmail, setTestEmail] = useState(() => readStored(STORAGE_TEST_EMAIL));
  const [replyTo, setReplyTo] = useState(() => readStored(STORAGE_REPLY_TO));
  const [windowDate, setWindowDate] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [groupByClient, setGroupByClient] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewSiteId, setPreviewSiteId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, label: '' });
  const [historyEnvoi, setHistoryEnvoi] = useState(null);
  const [resending, setResending] = useState(false);
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [editingEmailValue, setEditingEmailValue] = useState('');
  const cancelRef = useRef(false);

  const extraVars = useMemo(() => ({
    date: formatDateFr(windowDate),
    heure_debut: windowStart,
    heure_fin: windowEnd,
  }), [windowDate, windowStart, windowEnd]);

  const loadFacets = useCallback(async () => {
    try {
      setFacets(await itiMaintenanceService.facets());
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les filtres');
    }
  }, []);

  const loadSites = useCallback(async () => {
    setLoading(true);
    try {
      const data = await itiMaintenanceService.listSites(filters);
      setSites(data.items);
      setBasket((prev) => {
        const next = new Map(prev);
        data.items.forEach((site) => {
          if (!next.has(site.id)) return;
          next.set(site.id, { ...next.get(site.id), ...site });
        });
        return next;
      });
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les sites');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadTemplates = useCallback(async () => {
    try {
      setTemplates(await itiMaintenanceService.listTemplates());
    } catch (err) {
      notify.error(err.message || 'Impossible de charger les modèles');
    }
  }, []);

  const loadEnvois = useCallback(async () => {
    try {
      const data = await itiMaintenanceService.listEnvois(1, 30);
      setEnvois(data.items);
    } catch (err) {
      notify.error(err.message || 'Impossible de charger l’historique');
    }
  }, []);

  useEffect(() => {
    loadFacets();
    loadTemplates();
    loadEnvois();
  }, [loadFacets, loadTemplates, loadEnvois]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const basketSites = useMemo(() => [...basket.values()], [basket]);
  const grouped = useMemo(
    () => groupRecipients(basketSites, groupByClient, emailOverrides),
    [basketSites, groupByClient, emailOverrides],
  );
  const sendableCount = grouped.filter((group) => group.email).length;
  const allFilteredSelected = sites.length > 0 && sites.every((site) => basket.has(site.id));
  const previewOptions = basketSites.length > 0 ? basketSites : sites;

  const pluginSuggestions = useMemo(() => {
    const q = pluginQuery.trim().toLowerCase();
    return facets.plugins
      .filter((plugin) => {
        if (filters.plugins.includes(plugin.slug) || filters.plugins.includes(plugin.name)) {
          return false;
        }
        if (!q) return true;
        return plugin.name.toLowerCase().includes(q) || plugin.slug.toLowerCase().includes(q);
      })
      .slice(0, 12);
  }, [facets.plugins, filters.plugins, pluginQuery]);

  const applySiteUpdates = (items) => {
    const byId = new Map(items.map((site) => [site.id, site]));
    setSites((prev) => prev.map((site) => (byId.has(site.id) ? { ...site, ...byId.get(site.id) } : site)));
    setBasket((prev) => {
      const next = new Map(prev);
      items.forEach((site) => {
        if (!next.has(site.id)) return;
        next.set(site.id, { ...next.get(site.id), ...site });
      });
      return next;
    });
  };

  const addSitesToBasket = (list) => {
    setBasket((prev) => {
      const next = new Map(prev);
      list.forEach((site) => next.set(site.id, { ...next.get(site.id), ...site }));
      return next;
    });
  };

  const removeFromBasket = (id) => {
    setBasket((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const applyTemplate = (id) => {
    setTemplateId(id);
    const template = templates.find((item) => String(item.id) === String(id));
    if (!template) return;
    setSubject(template.subject);
    setBody(template.body_html);
    setTemplateName(template.name);
  };

  const insertVariable = (token) => {
    setBody((prev) => `${prev}${prev && !prev.endsWith('\n') ? ' ' : ''}${token}`);
  };

  const handlePreview = async () => {
    const site = previewOptions.find((item) => String(item.id) === String(previewSiteId)) || previewOptions[0];
    if (!site) {
      notify.warning('Aucun site à prévisualiser');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      notify.warning('Renseignez l’objet et le corps du mail');
      return;
    }
    setPreviewing(true);
    try {
      const group = grouped.find((item) => item.sites.some((candidate) => candidate.id === site.id));
      const data = await itiMaintenanceService.preview({
        hebergement_ids: group ? group.sites.map((item) => item.id) : [site.id],
        subject,
        body_html: body,
        extra_vars: extraVars,
        email_overrides: emailOverrides,
      });
      setPreview(data);
    } catch (err) {
      notify.error(err.message || 'Aperçu impossible');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSaveTemplate = async () => {
    const name = templateName.trim() || subject.trim();
    if (!name || !subject.trim() || !body.trim()) {
      notify.warning('Nom, objet et corps sont requis pour enregistrer un modèle');
      return;
    }
    try {
      const saved = await itiMaintenanceService.saveTemplate(
        { name, subject, body_html: body },
        templateId ? Number(templateId) : null,
      );
      notify.success(templateId ? 'Modèle mis à jour' : 'Modèle enregistré');
      await loadTemplates();
      setTemplateId(String(saved.id));
      setTemplateName(saved.name);
    } catch (err) {
      notify.error(err.message || 'Enregistrement du modèle impossible');
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateId) return;
    if (!(await ask({
      title: 'Supprimer le modèle',
      message: 'Supprimer ce modèle ?',
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    }))) return;
    try {
      await itiMaintenanceService.deleteTemplate(Number(templateId));
      setTemplateId('');
      setTemplateName('');
      await loadTemplates();
      notify.success('Modèle supprimé');
    } catch (err) {
      notify.error(err.message || 'Suppression impossible');
    }
  };

  const openConfirm = () => {
    if (basketSites.length === 0) {
      notify.warning('Ajoutez au moins un site au panier');
      return;
    }
    if (!subject.trim() || !body.trim()) {
      notify.warning('Renseignez l’objet et le corps du mail');
      return;
    }
    if (testMode && !testEmail.trim()) {
      notify.warning('Saisissez une adresse de test');
      return;
    }
    if (!testMode && sendableCount === 0) {
      notify.warning('Aucun destinataire du panier n’a d’adresse e-mail');
      return;
    }
    if (replyTo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyTo.trim())) {
      notify.warning('Adresse Reply-To invalide');
      return;
    }
    writeStored(STORAGE_TEST_EMAIL, testEmail.trim());
    writeStored(STORAGE_REPLY_TO, replyTo.trim());
    setConfirmOpen(true);
  };

  const runSend = async () => {
    cancelRef.current = false;
    setSending(true);
    const sendable = grouped.filter((group) => group.email);
    const skipped = grouped.filter((group) => !group.email);
    const recipients = skipped.map((group) => ({
      hebergement_id: group.sites[0].id,
      hebergement_ids: group.sites.map((site) => site.id),
      name: group.sites.map((site) => site.name).join(', '),
      url: group.sites[0].url,
      client: group.client || null,
      email: null,
      status: 'skipped',
      error: 'Aucune adresse e-mail client.',
    }));

    const payloadBase = {
      subject,
      body_html: body,
      template_id: templateId ? Number(templateId) : null,
      test_mode: testMode,
      test_email: testMode ? testEmail.trim() : null,
      reply_to: replyTo.trim() || null,
      extra_vars: extraVars,
      email_overrides: emailOverrides,
      group_by_client: groupByClient,
      filters,
    };

    let index = 0;
    setProgress({ current: 0, total: sendable.length, label: '' });
    for (const group of sendable) {
      if (cancelRef.current) break;
      setProgress({ current: index, total: sendable.length, label: group.sites.map((site) => site.name).join(', ') });
      try {
        const data = await itiMaintenanceService.sendOne({
          ...payloadBase,
          hebergement_ids: group.sites.map((site) => site.id),
        });
        recipients.push(data.recipient);
      } catch (err) {
        recipients.push({
          hebergement_id: group.sites[0].id,
          hebergement_ids: group.sites.map((site) => site.id),
          name: group.sites.map((site) => site.name).join(', '),
          url: group.sites[0].url,
          client: group.client || null,
          email: group.email,
          status: 'error',
          error: err.message || 'Envoi impossible',
        });
      }
      index += 1;
      setProgress({ current: index, total: sendable.length, label: group.sites.map((site) => site.name).join(', ') });
    }

    const sentCount = recipients.filter((item) => item.status === 'sent').length;
    const errorCount = recipients.filter((item) => item.status === 'error').length;
    const aborted = cancelRef.current;

    try {
      if (sentCount > 0 || errorCount > 0) {
        const result = await itiMaintenanceService.recordEnvoi({
          ...payloadBase,
          recipients,
        });
        notify.success(
          `${result.sent_count} mail(s) envoyé(s)`
          + (result.skipped_count ? ` · ${result.skipped_count} ignoré(s)` : '')
          + (result.error_count ? ` · ${result.error_count} erreur(s)` : '')
          + (aborted ? ' · interrompu' : ''),
        );
        setBasket(new Map());
        await loadEnvois();
        setTab('historique');
        setHistoryEnvoi(result);
      } else if (aborted) {
        notify.warning('Envoi interrompu, aucun mail envoyé');
      } else {
        notify.warning('Aucun mail envoyé');
      }
    } catch (err) {
      notify.error(err.message || 'Journalisation de l’envoi impossible');
    } finally {
      setSending(false);
      setConfirmOpen(false);
      setProgress({ current: 0, total: 0, label: '' });
    }
  };

  const handleRefreshEmails = async () => {
    const ids = (basketSites.length > 0 ? basketSites : sites).map((site) => site.id);
    if (ids.length === 0) {
      notify.warning('Aucun site à rafraîchir');
      return;
    }
    setRefreshing(true);
    try {
      const items = await itiMaintenanceService.refreshEmails(ids);
      applySiteUpdates(items);
      notify.success(`${items.filter((site) => site.has_email).length} e-mail(s) trouvé(s) sur ${items.length} site(s)`);
    } catch (err) {
      notify.error(err.message || 'Rafraîchissement impossible');
    } finally {
      setRefreshing(false);
    }
  };

  const saveEditedEmail = async (site) => {
    const trimmed = editingEmailValue.trim();
    setEmailOverrides((prev) => {
      const next = { ...prev };
      if (trimmed) next[site.id] = trimmed;
      else delete next[site.id];
      return next;
    });
    const patched = { ...site, email: trimmed || null, has_email: Boolean(trimmed) };
    applySiteUpdates([patched]);
    setEditingEmailId(null);
    if (!trimmed) return;
    if (!site.has_client) {
      notify.info('Adresse utilisée pour cet envoi uniquement (client non lié).');
      return;
    }
    try {
      const updated = await itiMaintenanceService.updateSiteEmail(site.id, trimmed);
      applySiteUpdates([updated]);
      setEmailOverrides((prev) => {
        const next = { ...prev };
        delete next[site.id];
        return next;
      });
      notify.success('E-mail enregistré sur la fiche client');
    } catch (err) {
      notify.warning(err.message || 'E-mail conservé pour cet envoi seulement');
    }
  };

  const handleResendErrors = async () => {
    if (!historyEnvoi?.id) return;
    setResending(true);
    try {
      const result = await itiMaintenanceService.resendErrors(historyEnvoi.id, {
        reply_to: replyTo.trim() || null,
        extra_vars: extraVars,
      });
      notify.success(`${result.sent_count} mail(s) renvoyé(s)`);
      await loadEnvois();
      setHistoryEnvoi(result);
    } catch (err) {
      notify.error(err.message || 'Renvoi impossible');
    } finally {
      setResending(false);
    }
  };

  const renderTag = (label, active, onClick, count) => (
    <button
      key={label}
      type="button"
      className={`iti-maint-tag${active ? ' is-active' : ''}`}
      onClick={onClick}
    >
      {label}{count != null ? ` (${count})` : ''}
    </button>
  );

  const filtersAreEmpty = searchInput === ''
    && filters.search === ''
    && filters.servers.length === 0
    && filters.plugins.length === 0
    && filters.tags.length === 0
    && filters.hasEmail == null
    && filters.hasClient == null;

  return (
    <MasterLayout>
      <Breadcrumb
        title="Mails sites"
        subtitle="Sélectionnez les sites, filtrez par tags, puis envoyez un mail personnalisé."
      />
      <div className="kkp-planning-page iti-maintenance-page d-flex flex-column gap-3">
        <div className="kkp-planning-view-toolbar justify-content-start">
          <AppPillSwitch
            options={[
              { value: 'destinataires', label: 'Destinataires', icon: 'mdi:web', count: sites.length },
              { value: 'historique', label: 'Historique', icon: 'mdi:history', count: envois.length },
            ]}
            value={tab}
            onChange={setTab}
            ariaLabel="Onglets maintenance mails"
          />
        </div>

        {tab === 'destinataires' && (
          <>
            <AppFilterBar
              onReset={() => {
                setSearchInput('');
                setFilters(emptyFilters());
              }}
              resetDisabled={filtersAreEmpty}
            >
              <AppFilterInput
                label="Recherche"
                value={searchInput}
                onChange={setSearchInput}
                placeholder="Client, site, URL…"
              />
              <AppFilterSelect
                id="iti-maint-email"
                label="E-mail client"
                multiple={false}
                value={filters.hasEmail === true ? ['1'] : filters.hasEmail === false ? ['0'] : []}
                onChange={(next) => {
                  const value = next[0];
                  setFilters((prev) => ({
                    ...prev,
                    hasEmail: value === '1' ? true : value === '0' ? false : null,
                  }));
                }}
                options={[
                  { value: '1', label: 'Avec e-mail' },
                  { value: '0', label: 'Sans e-mail' },
                ]}
                placeholder="Tous"
              />
              <AppFilterSelect
                id="iti-maint-client"
                label="Client Dolibarr"
                multiple={false}
                value={filters.hasClient === true ? ['1'] : filters.hasClient === false ? ['0'] : []}
                onChange={(next) => {
                  const value = next[0];
                  setFilters((prev) => ({
                    ...prev,
                    hasClient: value === '1' ? true : value === '0' ? false : null,
                  }));
                }}
                options={[
                  { value: '1', label: 'Lié' },
                  { value: '0', label: 'Non lié' },
                ]}
                placeholder="Tous"
              />
            </AppFilterBar>

            <div className="iti-maint-card">
              <div className="iti-maint-tag-group">
                <div className="iti-maint-tag-group__label">Serveurs</div>
                <div className="iti-maint-tags">
                  {facets.servers.map((server) => renderTag(
                    server.label,
                    filters.servers.includes(server.key),
                    () => setFilters((prev) => ({ ...prev, servers: toggleValue(prev.servers, server.key) })),
                    server.count,
                  ))}
                  {facets.servers.length === 0 && <span className="text-muted small">Aucun serveur</span>}
                </div>
              </div>
              <div className="iti-maint-tag-group">
                <div className="iti-maint-tag-group__label">Tags MainWP</div>
                <div className="iti-maint-tags">
                  {facets.tags.slice(0, 24).map((tag) => renderTag(
                    tag.name,
                    filters.tags.includes(tag.name),
                    () => setFilters((prev) => ({ ...prev, tags: toggleValue(prev.tags, tag.name) })),
                    tag.count,
                  ))}
                  {facets.tags.length === 0 && <span className="text-muted small">Aucun tag</span>}
                </div>
              </div>
              <div className="iti-maint-tag-group mb-0">
                <div className="iti-maint-tag-group__label">Plugins</div>
                <div className="iti-maint-tags mb-2">
                  {filters.plugins.map((plugin) => renderTag(
                    plugin,
                    true,
                    () => setFilters((prev) => ({ ...prev, plugins: prev.plugins.filter((item) => item !== plugin) })),
                  ))}
                </div>
                <AppFilterField label="Rechercher un plugin" col="col-12 col-md-6 col-lg-4">
                  <AppInput
                    value={pluginQuery}
                    onChange={(e) => setPluginQuery(e.target.value)}
                    placeholder="woocommerce, yoast…"
                  />
                </AppFilterField>
                <div className="iti-maint-tags mt-2">
                  {pluginSuggestions.map((plugin) => renderTag(
                    `${plugin.name}`,
                    false,
                    () => {
                      setFilters((prev) => ({ ...prev, plugins: [...prev.plugins, plugin.slug] }));
                      setPluginQuery('');
                    },
                    plugin.count,
                  ))}
                </div>
              </div>
            </div>

            <div className="iti-maint-layout">
              <div className="iti-maint-card">
                <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
                  <strong>{sites.length} site(s)</strong>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <AppButton
                      size="sm"
                      variant="outline-secondary"
                      onClick={handleRefreshEmails}
                      loading={refreshing}
                      loadingLabel="Rafraîchissement…"
                    >
                      Rafraîchir les e-mails
                    </AppButton>
                    <AppButton
                      size="sm"
                      variant="outline-primary"
                      onClick={() => {
                        if (allFilteredSelected) {
                          setBasket((prev) => {
                            const next = new Map(prev);
                            sites.forEach((site) => next.delete(site.id));
                            return next;
                          });
                          return;
                        }
                        addSitesToBasket(sites);
                      }}
                      disabled={sites.length === 0}
                    >
                      {allFilteredSelected ? 'Retirer le filtre du panier' : 'Ajouter le filtre au panier'}
                    </AppButton>
                  </div>
                </div>

                {basketSites.length > 0 && (
                  <div className="iti-maint-basket mb-3">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                      <strong>Panier · {basketSites.length} site(s) · {sendableCount} mail(s)</strong>
                      <AppButton variant="link" size="sm" onClick={() => setBasket(new Map())}>
                        Vider
                      </AppButton>
                    </div>
                    <div className="iti-maint-tags">
                      {basketSites.map((site) => (
                        <button
                          key={site.id}
                          type="button"
                          className="iti-maint-tag is-active"
                          onClick={() => removeFromBasket(site.id)}
                          title="Retirer du panier"
                        >
                          {site.name} ×
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="iti-maint-table-wrap">
                  <StickyTableResponsive embedded>
                    <table className="table table-sm align-middle mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }} />
                          <th>Site</th>
                          <th>Client</th>
                          <th>Serveur</th>
                          <th>E-mail</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading && (
                          <tr><td colSpan={5} className="text-muted">Chargement…</td></tr>
                        )}
                        {!loading && sites.length === 0 && (
                          <tr><td colSpan={5} className="text-muted">Aucun site pour ces filtres.</td></tr>
                        )}
                        {!loading && sites.map((site) => (
                          <tr key={site.id} className={basket.has(site.id) ? 'iti-maint-row-selected' : ''}>
                            <td>
                              <AppCheckbox
                                checked={basket.has(site.id)}
                                onChange={() => {
                                  if (basket.has(site.id)) removeFromBasket(site.id);
                                  else addSitesToBasket([site]);
                                }}
                              />
                            </td>
                            <td>
                              <div className="fw-semibold">{site.name}</div>
                              <a href={site.url} target="_blank" rel="noreferrer" className="small">{site.url}</a>
                            </td>
                            <td>{site.client_nom || <span className="text-muted">Non lié</span>}</td>
                            <td>{site.server_label || '—'}</td>
                            <td>
                              <ItiMaintenanceEmailEditor
                                site={site}
                                overrides={emailOverrides}
                                editingId={editingEmailId}
                                editingValue={editingEmailValue}
                                onStart={(current) => {
                                  setEditingEmailId(current.id);
                                  setEditingEmailValue(emailOverrides[current.id] || current.email || '');
                                }}
                                onChange={setEditingEmailValue}
                                onCancel={() => setEditingEmailId(null)}
                                onSave={saveEditedEmail}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </StickyTableResponsive>
                </div>
              </div>

              <div className="iti-maint-card iti-maint-composer">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Mail</strong>
                  <span className="small text-muted">Variables : {VARIABLES.map((item) => item.token).join(' ')}</span>
                </div>
                <AppFormField className="mb-2" label="Modèle">
                  <div className="d-flex gap-2">
                    <AppSelect
                      value={templateId}
                      onChange={(e) => applyTemplate(e.target.value)}
                    >
                      <option value="">Nouveau mail</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))}
                    </AppSelect>
                    {templateId && (
                      <AppButton variant="outline-danger" icon="mdi:delete-outline" iconWidth={18} onClick={handleDeleteTemplate} title="Supprimer le modèle" />
                    )}
                  </div>
                </AppFormField>
                <AppFormField className="mb-2" label="Nom du modèle">
                  <AppInput
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Ex. Maintenance serveur 1"
                  />
                </AppFormField>
                <AppFormField className="mb-2" label="Objet">
                  <AppInput
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Maintenance {{site}} — {{date}}"
                  />
                </AppFormField>
                <div className="row g-2 mb-2">
                  <div className="col-12">
                    <AppFormLabel>Créneau</AppFormLabel>
                  </div>
                  <div className="col-6">
                    <AppInput
                      type="date"
                      value={windowDate}
                      onChange={(e) => setWindowDate(e.target.value)}
                    />
                  </div>
                  <div className="col-3">
                    <AppInput
                      type="time"
                      value={windowStart}
                      onChange={(e) => setWindowStart(e.target.value)}
                      title="Heure de début"
                    />
                  </div>
                  <div className="col-3">
                    <AppInput
                      type="time"
                      value={windowEnd}
                      onChange={(e) => setWindowEnd(e.target.value)}
                      title="Heure de fin"
                    />
                  </div>
                </div>
                <AppFormField className="mb-2" label="Corps">
                  <div className="d-flex gap-1 flex-wrap justify-content-end mb-2">
                    {VARIABLES.map((item) => (
                      <AppButton
                        key={item.token}
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => insertVariable(item.token)}
                      >
                        {item.label}
                      </AppButton>
                    ))}
                  </div>
                  <AppTextarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </AppFormField>
                <AppFormField className="mb-2" label="Reply-To">
                  <AppInput
                    type="email"
                    value={replyTo}
                    onChange={(e) => setReplyTo(e.target.value)}
                    placeholder="support@iti-conseil.com"
                  />
                </AppFormField>
                <AppCheckbox
                  id="iti-maint-group"
                  wrapperClassName="mb-2"
                  label="1 mail par client (liste les sites)"
                  checked={groupByClient}
                  onChange={(e) => setGroupByClient(e.target.checked)}
                />
                <AppCheckbox
                  id="iti-maint-test"
                  wrapperClassName="mb-2"
                  label="Mode test"
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                />
                {testMode && (
                  <AppFormField className="mb-2" label="E-mail de test">
                    <AppInput
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="vous@iti-conseil.com"
                    />
                  </AppFormField>
                )}
                <AppFormField className="mb-2" label="Aperçu pour">
                  <AppSelect
                    value={previewSiteId}
                    onChange={(e) => setPreviewSiteId(e.target.value)}
                  >
                    <option value="">Premier du panier / filtre</option>
                    {previewOptions.map((site) => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </AppSelect>
                </AppFormField>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <AppButton variant="outline-secondary" onClick={handleSaveTemplate}>
                    Enregistrer le modèle
                  </AppButton>
                  <AppButton variant="outline-primary" onClick={handlePreview} loading={previewing} loadingLabel="Aperçu…">
                    Aperçu
                  </AppButton>
                  <AppButton
                    onClick={openConfirm}
                    disabled={sending || basketSites.length === 0}
                    loading={sending}
                    loadingLabel="Envoi…"
                  >
                    {`Envoyer (${sendableCount})`}
                  </AppButton>
                </div>
                {preview && (
                  <div>
                    <div className="small text-muted mb-1">
                      Aperçu pour {preview.site}{preview.email ? ` · ${preview.email}` : ' · sans e-mail'}
                    </div>
                    <div className="fw-semibold mb-2">{preview.subject}</div>
                    <div className="iti-maint-preview">
                      <iframe title="Aperçu du mail" srcDoc={preview.html} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {tab === 'historique' && (
          <div className="iti-maint-card">
            {envois.length === 0 && <p className="text-muted mb-0">Aucun envoi pour le moment.</p>}
            {envois.map((envoi) => (
              <button
                key={envoi.id}
                type="button"
                className="iti-maint-history-item"
                onClick={() => setHistoryEnvoi(envoi)}
              >
                <div className="d-flex justify-content-between gap-2 flex-wrap">
                  <div>
                    <div className="fw-semibold">{envoi.subject}</div>
                    <div className="small text-muted">
                      {new Date(envoi.sent_at).toLocaleString('fr-FR')}
                      {envoi.sent_by ? ` · ${envoi.sent_by}` : ''}
                      {envoi.template_name ? ` · modèle ${envoi.template_name}` : ''}
                      {envoi.test_mode ? ' · TEST' : ''}
                    </div>
                  </div>
                  <div className="small">
                    {envoi.sent_count} envoyé(s)
                    {envoi.skipped_count ? ` · ${envoi.skipped_count} ignoré(s)` : ''}
                    {envoi.error_count ? ` · ${envoi.error_count} erreur(s)` : ''}
                  </div>
                </div>
                <div className="iti-maint-history-recipients mt-1">
                  {(envoi.recipients || []).slice(0, 8).map((recipient) => (
                    <span key={`${envoi.id}-${recipient.hebergement_id}`} className="me-2">
                      {recipient.name} ({recipient.status})
                    </span>
                  ))}
                  {(envoi.recipients || []).length > 8 ? `… +${envoi.recipients.length - 8}` : ''}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <ItiMaintenanceConfirmModal
        open={confirmOpen}
        groups={grouped}
        overrides={emailOverrides}
        testMode={testMode}
        testEmail={testEmail}
        replyTo={replyTo}
        subject={subject}
        sending={sending}
        progress={progress}
        onClose={() => { if (!sending) setConfirmOpen(false); }}
        onConfirm={runSend}
        onAbort={() => { cancelRef.current = true; }}
      />
      <ItiMaintenanceHistoryModal
        envoi={historyEnvoi}
        onClose={() => setHistoryEnvoi(null)}
        onResend={handleResendErrors}
        resending={resending}
      />
      {modal}
    </MasterLayout>
  );
};

export default ItiMaintenanceMailsPage;
